/**
 * Hoe lang geleden, in woorden.
 *
 * ------------------------------------------------------------------
 * Dit stond drie keer los in de app — in MiniApp.js, in UiHelpers.kt en in
 * DetailLabels.swift — en alle drie kapten de dagen af. De server rondt ze af,
 * en met opzet: zie report_intelligence.standing_days, waar precies dezelfde
 * fout al eens van de weg gemeld is. "3 dagen en 21 uur" afkappen naar "3
 * dagen" leest als een fout, zeker naast een strook die per kalenderdag tekent.
 *
 * Gevolg van die twee verschillende regels, gemeld met twee schermafdrukken
 * naast elkaar: een telefoonflitser van 1 dag en 17 uur oud stond op het
 * kaartje als "1 dag" en op het detailscherm als "Staat hier al 2 dagen".
 * Allebei over dezelfde melding, op hetzelfde moment.
 *
 * Dus: onder de dag afkappen, vanaf de dag afronden. Die grens is geen
 * inconsistentie maar het verschil tussen twee vragen. "59 min" hoort geen
 * "1 uur" te worden — dat is nog geen uur geleden, en bij minuten en uren
 * kijk je naar de klok. Bij dagen kijk je naar een kalender, en dan is 1 d 17 u
 * in elk normaal gesprek "twee dagen".
 */

/** Wat korter is dan dit, blijft in uren staan. */
export const DAG_S = 86400;

/**
 * Dagen, afgerond zoals de server ze afrondt.
 *
 * Nooit negatief: een tijdstempel een paar seconden in de toekomst — een
 * aankondiging die net binnenkwam — hoort niet als iets raars te lezen.
 */
export const dagenAfgerond = (seconden) => Math.round(Math.max(0, seconden) / DAG_S);

/**
 * "Zojuist" / "12 min" / "3 uur" / "2 dagen".
 *
 * `nowMs` komt van buiten zodat dit te toetsen is zonder de klok te zetten.
 */
export function timeAgo(timestamp, nowMs = Date.now()) {
  // Eerst op ontbreken toetsen en niet op NaN. `new Date(null)` is niet
  // ongeldig maar 1 januari 1970, dus een melding zonder tijdstempel las als
  // "20720 dagen". Die fout zat ook in de versie die hiervoor in MiniApp.js
  // stond, en hij is hier pas opgevallen omdat dit nu een eigen test heeft.
  if (timestamp == null || timestamp === "") return "";
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (s < 60) return "Zojuist";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < DAG_S) return `${Math.floor(s / 3600)} uur`;
  const dagen = dagenAfgerond(s);
  return `${dagen} ${dagen === 1 ? "dag" : "dagen"}`;
}

/**
 * "2× vrijgegeven", of "2 van de 3 vrijgegeven" zolang de drempel niet
 * gehaald is.
 *
 * De noemer is de hele reden dat deze functie bestaat. Van de weg gemeld: een
 * melding met twee vrijgaven stond in Telegram als "🔴 2× vrijgegeven" terwijl
 * de app hem "Actieve melding" noemde. Allebei waar — een paal vraagt er drie,
 * een controle twee — maar zonder dat getal erbij lezen ze als tegenspraak.
 *
 * Boven de drempel valt de noemer weg: dan staat de status er zelf al
 * ("Mogelijk vrijgegeven") en voegt "3 van de 3" niets meer toe.
 *
 * Null als niemand iets gezegd heeft. Dat is geen regel om te tonen.
 */
export function vrijgegevenLabel(count, needed) {
  if (!count || count <= 0) return null;
  if (!needed || count >= needed) return `${count}× vrijgegeven`;
  return `${count} van de ${needed} vrijgegeven`;
}
